import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, walletAdminApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  ACTIVE:   { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  INACTIVE: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  BANNED:   { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
};

const Badge = ({ status }) => {
  const s = (status || '').toUpperCase();
  const c = STATUS_COLORS[s] || { bg: 'var(--bg-tertiary)', color: 'var(--text-muted)' };
  return (
    <span style={{ ...c, padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>
      {s}
    </span>
  );
};

// ─── Wallet Tab ────────────────────────────────────────────────────────────────
const WalletTab = ({ userId }) => {
  const [wallet,   setWallet]   = useState(null);
  const [txs,      setTxs]      = useState([]);
  const [wLoading, setWLoading] = useState(true);
  const [amount,   setAmount]   = useState('');
  const [source,   setSource]   = useState('');
  const [deduct,   setDeduct]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadWallet = async () => {
    setWLoading(true);
    try {
      const res = await walletAdminApi.getUserWallet(userId);
      setWallet(res.data?.data?.wallet || res.data?.data);
      setTxs(res.data?.data?.transactions || []);
    } catch { toast.error('Failed to load wallet'); }
    finally { setWLoading(false); }
  };

  useEffect(() => { if (userId) loadWallet(); }, [userId]);

  const handleCoinAction = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setSubmitting(true);
    try {
      const payload = { userId, amount: amt, source: source || (deduct ? 'Admin Deduct' : 'Admin Bonus') };
      if (deduct) await walletAdminApi.deductCoins(payload);
      else        await walletAdminApi.addCoins(payload);
      toast.success(deduct ? `${amt} coins deducted` : `${amt} coins added`);
      setAmount(''); setSource('');
      loadWallet();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  if (wLoading) return <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Loading wallet…</div>;

  return (
    <div style={{ padding: '0 24px 24px' }}>
      {/* Balance card */}
      <div style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px', color: 'white' }}>
        <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '6px' }}>Current Balance</div>
        <div style={{ fontSize: '2rem', fontWeight: 800 }}>🪙 {wallet?.balance?.toLocaleString() ?? '—'}</div>
        <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: '8px', display: 'flex', gap: '16px' }}>
          <span>Earned: +{wallet?.totalEarned?.toLocaleString() ?? 0}</span>
          <span>Spent: -{wallet?.totalSpent?.toLocaleString() ?? 0}</span>
        </div>
      </div>

      {/* Add / Deduct coins */}
      <div style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '12px' }}>💰 Manage Coins</div>
        {/* Toggle */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {[false, true].map(d => (
            <button key={String(d)} onClick={() => setDeduct(d)}
              style={{ flex: 1, padding: '7px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem',
                background: deduct === d ? (d ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)') : 'var(--bg-primary)',
                color: deduct === d ? (d ? '#ef4444' : '#22c55e') : 'var(--text-muted)' }}>
              {d ? '⬇️ Deduct Coins' : '⬆️ Add Coins'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input type="number" placeholder="Amount" value={amount} onChange={e => setAmount(e.target.value)}
            style={{ flex: 1, padding: '9px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' }} />
          <input type="text" placeholder="Source / Reason" value={source} onChange={e => setSource(e.target.value)}
            style={{ flex: 2, padding: '9px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none' }} />
          <button onClick={handleCoinAction} disabled={submitting}
            style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
              background: deduct ? '#ef4444' : '#22c55e', color: 'white', opacity: submitting ? 0.6 : 1 }}>
            {submitting ? '…' : (deduct ? 'Deduct' : 'Add')}
          </button>
        </div>
      </div>

      {/* Recent transactions */}
      <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '10px' }}>Recent Transactions</div>
      {txs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>No transactions yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflowY: 'auto' }}>
          {txs.slice(0, 20).map(tx => (
            <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 600 }}>{tx.category?.replace('_', ' ')} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {tx.source}</span></div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{tx.createdAt ? new Date(tx.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
              </div>
              <span style={{ fontWeight: 800, fontSize: '0.95rem', color: tx.type === 'EARN' ? '#22c55e' : '#ef4444' }}>
                {tx.type === 'EARN' ? '+' : ''}{tx.amount?.toLocaleString()} 🪙
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── User Detail Modal ─────────────────────────────────────────────────────────
const UserDetailModal = ({ userId, onClose, onStatusChange }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState('details'); // 'details' | 'wallet'

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    usersApi.getById(userId)
      .then(res => setUser(res.data?.data))
      .catch(() => toast.error('Failed to load user details'))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleStatus = async (status) => {
    try {
      await usersApi.updateStatus(userId, status);
      toast.success(`User ${status.toLowerCase()}`);
      onStatusChange();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  return (
    <div style={overlayS}>
      <div style={{ ...modalS, maxWidth: '560px' }}>
        <div style={modalHeaderS}>
          <h3 style={{ fontWeight: 700 }}>User Details</h3>
          <button style={closeBtnS} onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Loading...</div>
        ) : !user ? (
          <div style={{ textAlign: 'center', padding: '32px', color: 'var(--accent-danger)' }}>User not found</div>
        ) : (
          <>
            {/* Avatar & name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '16px 24px', padding: '14px 16px', background: 'var(--bg-tertiary)', borderRadius: '12px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                {user.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{user.name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{user.email}</div>
              </div>
              <div style={{ marginLeft: 'auto' }}><Badge status={user.status} /></div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', margin: '0 24px 16px', background: 'var(--bg-tertiary)', padding: '4px', borderRadius: '10px' }}>
              {[{ id: 'details', label: '👤 Details' }, { id: 'wallet', label: '🪙 Wallet' }].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                    background: tab === t.id ? 'var(--bg-card)' : 'transparent',
                    color: tab === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
                    boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.15)' : 'none' }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            {tab === 'details' ? (
              <div style={{ padding: '0 24px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                  {[
                    { label: 'Mobile',    value: user.mobile },
                    { label: 'City',      value: user.city },
                    { label: 'State',     value: user.state },
                    { label: 'Country',   value: user.country },
                    { label: 'User Type', value: user.userType },
                    { label: 'Level',     value: user.level },
                    { label: 'XP Points', value: user.xpPoints?.toLocaleString() },
                    { label: 'Total KM',  value: `${user.totalKm || 0} km` },
                    { label: 'Joined',    value: user.joinedAt ? new Date(user.joinedAt).toLocaleDateString('en-IN') : '—' },
                  ].map((f, i) => (
                    <div key={i} style={{ background: 'var(--bg-primary)', padding: '10px 12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', marginBottom: '3px' }}>{f.label}</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>{f.value || '—'}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {user.status !== 'ACTIVE'   && <button style={{ ...btnS, background: 'rgba(34,197,94,0.15)',  color: '#22c55e'  }} onClick={() => handleStatus('ACTIVE')}>✅ Activate</button>}
                  {user.status !== 'INACTIVE' && <button style={{ ...btnS, background: 'rgba(245,158,11,0.15)', color: '#f59e0b'  }} onClick={() => handleStatus('INACTIVE')}>😴 Deactivate</button>}
                  {user.status !== 'BANNED'   && <button style={{ ...btnS, background: 'rgba(239,68,68,0.15)',  color: '#ef4444'  }} onClick={() => handleStatus('BANNED')}>🚫 Ban</button>}
                </div>
              </div>
            ) : (
              <WalletTab userId={userId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const UserManagement = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('users.edit') || hasPermission('*');

  const [users,    setUsers]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);  // userId for detail modal

  const PAGE_SIZE = 15;

  const fetchUsers = useCallback(() => {
    setLoading(true);
    usersApi.getAll({ page, size: PAGE_SIZE, search, status: statusFilter })
      .then(res => {
        const d = res.data;
        setUsers(d.data || []);
        setTotal(d.total || 0);
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to fetch users'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ─── CSV Export ───────────────────────────────────────────────────────────
  const downloadCSV = () => {
    if (!users.length) return;
    const headers = ['ID', 'Name', 'Email', 'Mobile', 'City', 'Status', 'User Type', 'Level', 'XP', 'Total KM', 'Joined'];
    const rows = users.map(u => [
      u.id, u.name, u.email, u.mobile || '', u.city || '', u.status,
      u.userType, u.level, u.xpPoints, u.totalKm,
      u.joinedAt ? new Date(u.joinedAt).toLocaleDateString('en-IN') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'users.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>Home</span> / <span className="current">User Management</span></div>
          <h1>User Management</h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} total app users
          </p>
        </div>
        <button onClick={downloadCSV} style={{ ...btnS, background: 'rgba(99,102,241,0.15)', color: 'var(--accent-primary)', border: '1px solid rgba(99,102,241,0.3)' }}>
          📥 Export CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', flex: '1', minWidth: '200px' }}>
          <input
            style={inputS}
            placeholder="🔍 Search by name, email, phone..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <button type="submit" style={{ ...btnS, background: 'var(--accent-primary)', color: 'white', flexShrink: 0 }}>Search</button>
        </form>

        <select style={{ ...inputS, width: '160px', flexShrink: 0 }}
          value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="BANNED">Banned</option>
        </select>

        {(search || statusFilter) && (
          <button style={{ ...btnS, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
            onClick={() => { setSearch(''); setSearchInput(''); setStatusFilter(''); setPage(0); }}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
                {['#', 'User', 'Mobile', 'City', 'Status', 'Type', 'Level / XP', 'Joined', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div style={miniSpinSt} /> Loading users...
                  </div>
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
                  No users found
                </td></tr>
              ) : users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={tdS}>{page * PAGE_SIZE + i + 1}</td>
                  <td style={tdS}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, color: 'white', flexShrink: 0 }}>
                        {u.name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{u.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdS}><span style={{ fontSize: '0.85rem' }}>{u.mobile || '—'}</span></td>
                  <td style={tdS}><span style={{ fontSize: '0.85rem' }}>{u.city || '—'}</span></td>
                  <td style={tdS}><Badge status={u.status} /></td>
                  <td style={tdS}><span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{u.userType}</span></td>
                  <td style={tdS}>
                    <span style={{ fontSize: '0.82rem' }}>Lv.{u.level} / </span>
                    <span style={{ fontSize: '0.82rem', color: '#f59e0b' }}>{u.xpPoints?.toLocaleString()} XP</span>
                  </td>
                  <td style={tdS}><span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    {u.joinedAt ? new Date(u.joinedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                  </span></td>
                  <td style={tdS}>
                    <button style={{ ...btnS, padding: '5px 12px', fontSize: '0.78rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                      onClick={() => setSelected(u.id)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ ...btnS, padding: '5px 12px', opacity: page === 0 ? 0.4 : 1 }}>‹ Prev</button>
              <span style={{ padding: '6px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ ...btnS, padding: '5px 12px', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next ›</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <UserDetailModal
          userId={selected}
          onClose={() => setSelected(null)}
          onStatusChange={fetchUsers}
        />
      )}
    </div>
  );
};

// ─── Shared styles ─────────────────────────────────────────────────────────────
const overlayS = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px',
};
const modalS = {
  background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
  borderRadius: '16px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: 'var(--shadow-lg)',
};
const modalHeaderS = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '20px 24px', borderBottom: '1px solid var(--border-color)',
};
const closeBtnS = {
  background: 'none', border: 'none', color: 'var(--text-muted)',
  cursor: 'pointer', fontSize: '1.2rem', padding: '4px',
};
const btnS = {
  padding: '8px 16px', borderRadius: '8px', border: 'none',
  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
  background: 'var(--bg-tertiary)', color: 'var(--text-primary)',
  display: 'flex', alignItems: 'center', gap: '6px',
  whiteSpace: 'nowrap',
};
const inputS = {
  padding: '9px 14px', background: 'var(--bg-tertiary)',
  border: '1px solid var(--border-color)', borderRadius: '8px',
  color: 'var(--text-primary)', fontSize: '0.88rem', outline: 'none', width: '100%',
};
const tdS = { padding: '12px 16px', fontSize: '0.88rem' };
const miniSpinSt = {
  width: '18px', height: '18px', border: '2px solid var(--border-color)',
  borderTopColor: 'var(--accent-primary)', borderRadius: '50%',
  animation: 'spin 0.7s linear infinite',
};

export default UserManagement;
